import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Card, CardContent, CardHeader, Stack, Typography, Divider, Chip } from '@mui/material';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const DEFAULT_CENTER = [37.8, -96];
const MAP_HEIGHT = 480;
const PALETTE = ['#1d4ed8', '#c026d3', '#059669', '#d97706', '#0d9488', '#9333ea', '#f43f5e', '#5c6bc0'];

const normalizeGeoJson = (feature) => {
  if (!feature) return null;

  const baseProps = {
    territory: feature?.properties?.territory || 'No territory label',
    passes_filter: feature?.properties?.passes_filter !== false,
    zip: feature?.properties?.zip,
    ...feature?.properties,
  };

  // Convert any input (Feature, FeatureCollection, or bare geometry) into a Leaflet-friendly shape.
  const toFeature = (node, inheritedProps = {}) => {
    if (!node) return null;
    if (node.type === 'FeatureCollection') {
      const features = (node.features || [])
        .map((child) => toFeature(child, inheritedProps))
        .reduce((acc, child) => {
          if (!child) return acc;
          if (child.type === 'FeatureCollection') {
            return acc.concat(child.features || []);
          }
          acc.push(child);
          return acc;
        }, []);
      if (!features.length) return null;
      return { type: 'FeatureCollection', features };
    }
    if (node.type === 'Feature') {
      return toFeature(node.geometry, { ...inheritedProps, ...(node.properties || {}) });
    }
    if (!node.type) return null;
    return {
      type: 'Feature',
      geometry: node,
      properties: { ...baseProps, ...inheritedProps },
    };
  };

  const geojson = toFeature(feature, feature.properties || {});
  if (!geojson) return null;

  return {
    geojson,
    centroid: feature.centroid,
    properties: baseProps,
  };
};

const ProjectionZipMap = ({
  features = [],
  territories = [],
  subheader = 'Filtered ZIPs are shown with lighter opacity. Colors are per territory.',
  showFilterLegend = true,
}) => {
  const normalizedFeatures = useMemo(
    () =>
      (features || [])
        .map((f) => normalizeGeoJson(f))
        .filter(Boolean),
    [features]
  );

  const territoryColorMap = useMemo(() => {
    const map = {};
    let idx = 0;
    territories.forEach((t) => {
      const key = t.territory || 'No territory label';
      if (!map[key]) {
        map[key] = PALETTE[idx % PALETTE.length];
        idx += 1;
      }
    });
    normalizedFeatures.forEach((f) => {
      const key = f.properties?.territory || 'No territory label';
      if (!map[key]) {
        map[key] = PALETTE[idx % PALETTE.length];
        idx += 1;
      }
    });
    return map;
  }, [normalizedFeatures, territories]);

  const legendEntries = useMemo(() => Object.entries(territoryColorMap).sort(([a], [b]) => a.localeCompare(b)), [
    territoryColorMap,
  ]);

  const mapCenter = useMemo(() => {
    const withCentroid = normalizedFeatures.find((f) => f.centroid && f.centroid.length === 2);
    return withCentroid ? [withCentroid.centroid[0], withCentroid.centroid[1]] : DEFAULT_CENTER;
  }, [normalizedFeatures]);

  const mapBounds = useMemo(() => {
    const layers = normalizedFeatures.map((f) => L.geoJSON(f.geojson));
    if (!layers.length) return null;
    const combined = layers.reduce((acc, layer) => acc.extend(layer.getBounds()), L.latLngBounds());
    return combined.isValid() ? combined : null;
  }, [normalizedFeatures]);

  const style = (feature) => {
    const territory = feature.properties?.territory || 'No territory label';
    const passes = feature.properties?.passes_filter !== false;
    const color = territoryColorMap[territory] || '#1d4ed8';
    return {
      color,
      weight: 1,
      fillColor: color,
      fillOpacity: passes ? 0.5 : 0.2,
      opacity: passes ? 0.8 : 0.4,
    };
  };

  const onEachFeature = (feature, layer) => {
    const props = feature?.properties || {};
    const territory = props.territory || 'No territory label';
    const rawZip = props.zip || '';
    const zip = /^\d{4}$/.test(rawZip) ? `0${rawZip}` : rawZip || 'Unknown ZIP';
    const city = props.city || '';
    const state = props.state || '';
    const location = city || state ? `${city}${city && state ? ', ' : ''}${state}` : 'Location unavailable';
    const status = props.passes_filter === false ? 'Filtered out' : 'Included';
    layer.bindTooltip(
      `<div style="font-weight:700; font-size:0.9rem; margin-bottom:2px;">${territory}</div>
       <div><strong>${zip}</strong> • ${location}</div>
       <div style="margin-top:2px;">${status}</div>`,
      { sticky: true, direction: 'top' }
    );
  };

  const AutoFit = ({ bounds }) => {
    const map = useMap();
    React.useEffect(() => {
      if (!bounds) return;
      map.fitBounds(bounds, { padding: [24, 24] });
    }, [bounds, map]);
    return null;
  };

  return (
    <Card>
      <CardHeader
        title="Territory map"
        subheader={subheader}
        action={
          showFilterLegend ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', pr: 1 }}>
              <Chip size="small" label="Included" sx={{ bgcolor: 'success.light' }} />
              <Chip size="small" label="Filtered out" variant="outlined" />
            </Stack>
          ) : null
        }
      />
      <Divider />
      <CardContent sx={{ height: MAP_HEIGHT, p: 0 }}>
        <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
          <MapContainer center={mapCenter} zoom={6} scrollWheelZoom={false} style={{ height: '100%', width: '100%' }}>
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
              subdomains={['a', 'b', 'c', 'd']}
              maxZoom={19}
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            />
            <AutoFit bounds={mapBounds} />
            {normalizedFeatures.map((feature, idx) => (
              <GeoJSON
                key={`${feature.properties?.zip || idx}-${idx}`}
                data={feature.geojson}
                style={style}
                onEachFeature={onEachFeature}
              />
            ))}
          </MapContainer>
          {legendEntries.length ? (
            <Box
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 1000,
                bgcolor: 'rgba(255,255,255,0.92)',
                borderRadius: 2,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                px: 1.5,
                py: 1,
                minWidth: 180,
                maxWidth: 240,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                Territories ({legendEntries.length})
              </Typography>
              <Stack spacing={0.75} sx={{ mt: 1, maxHeight: 180, overflow: 'auto' }}>
                {legendEntries.map(([territory, color]) => (
                  <Chip
                    key={territory}
                    label={territory}
                    size="small"
                    sx={{ bgcolor: color, color: 'common.white', fontWeight: 600 }}
                  />
                ))}
              </Stack>
            </Box>
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );
};

ProjectionZipMap.propTypes = {
  features: PropTypes.arrayOf(PropTypes.object),
  territories: PropTypes.arrayOf(PropTypes.object),
  subheader: PropTypes.string,
  showFilterLegend: PropTypes.bool,
};

export default ProjectionZipMap;
